import React, { useState, useEffect } from 'react';
import {
    Paper, Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell
} from 'recharts';
import supabase from '../../supabaseClient';

const COLOR_DAYS = '#6B1D2F';

function AttendanceSummaryReport({ idConfig }) {

    const last6Months = Array.from({ length: 6 }, (_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - 5 + i);
        return {
            month: String(d.getMonth() + 1).padStart(2, '0'),
            year: d.getFullYear()
        };
    });

    const startDate = `${last6Months[0].year}-${last6Months[0].month}-01`;
    const today = new Date();
    const todayDate = today.toISOString().split('T')[0];

    const [reportData, setReportData] = useState([]);
    const [selectedMonthKey, setSelectedMonthKey] = useState(null);

    const fetchAttendanceForReport = async () => {
        if (!idConfig) return;
        const { data, error } = await supabase
            .from('attendance')
            .select(`
                id,
                employee_id,
                check_in,
                check_out,
                id_configuration,
                created_at,
                employees(
                    name,
                    surname
                )
            `)
            .eq('id_configuration', idConfig)
            .gte('check_in', `${startDate}T00:00:00`)
            .lte('check_in', `${todayDate}T00:00:00`);
        if (error) {
            console.log('Błąd odświezania:', error);
            return;
        }
        setReportData(data || []);
    };

    useEffect(() => { fetchAttendanceForReport(); }, [idConfig]);

    // Build per-month, per-employee data
    const empMonthMap = {};
    reportData.forEach(r => {
        if (!r.check_out) return;
        const monthKey = r.check_in.slice(0, 7);
        const date = r.check_in.slice(0, 10);
        const hours = (new Date(r.check_out) - new Date(r.check_in)) / 3_600_000;
        const empId = r.employee_id;

        if (!empMonthMap[monthKey]) empMonthMap[monthKey] = {};
        if (!empMonthMap[monthKey][empId]) {
            empMonthMap[monthKey][empId] = {
                name: r.employees.name,
                surname: r.employees.surname,
                daysSet: new Set(),
                hours: 0
            };
        }
        empMonthMap[monthKey][empId].daysSet.add(date);
        empMonthMap[monthKey][empId].hours += hours;
    });

    const monthlyData = last6Months.map(m => {
        const monthKey = `${m.year}-${m.month}`;
        const empMap = empMonthMap[monthKey] || {};
        const employees = Object.values(empMap)
            .map(e => ({ name: e.name, surname: e.surname, days: e.daysSet.size, hours: e.hours }))
            .sort((a, b) => b.days - a.days);
        return {
            label: `${m.month}.${m.year}`,
            monthKey,
            totalDays: employees.reduce((s, e) => s + e.days, 0),
            totalHours: parseFloat(employees.reduce((s, e) => s + e.hours, 0).toFixed(1)),
            employees
        };
    });

    const selectedMonth = monthlyData.find(m => m.monthKey === selectedMonthKey);

    const handleBarClick = (data) => {
        if (!data?.monthKey) return;
        setSelectedMonthKey(prev => prev === data.monthKey ? null : data.monthKey);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const monthData = monthlyData.find(m => m.label === label);
        return (
            <Paper sx={{ p: 1.5, border: '1px solid #eee' }} elevation={3}>
                <Typography variant="body2" fontWeight="bold" mb={0.5}>{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Dni: <strong>{payload[0]?.value}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Godziny: <strong>{monthData?.totalHours}</strong>
                </Typography>
            </Paper>
        );
    };

    return (
        <Box>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }} elevation={1}>
                <Typography variant="h6" mb={3}>Podsumowanie obecności — ostatnie 6 miesięcy</Typography>
                <Box sx={{ '& *': { outline: 'none' } }}>
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0e8e8" />
                        <XAxis dataKey="label" tick={{ fontSize: 13 }} />
                        <YAxis tick={{ fontSize: 12 }} label={{ value: 'Dni', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12 } }} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(107,29,47,0.06)' }} />
                        <Bar dataKey="totalDays" name="Dni" maxBarSize={60} fill={COLOR_DAYS} onClick={handleBarClick} style={{ cursor: 'pointer' }} />
                    </BarChart>
                </ResponsiveContainer>
                </Box>
                {!selectedMonthKey && (
                    <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
                        Kliknij słupek, aby zobaczyć szczegóły miesiąca
                    </Typography>
                )}
            </Paper>

            {selectedMonth && (
                <Paper sx={{ p: 3, borderRadius: 3 }} elevation={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Szczegóły — {selectedMonth.label}
                        </Typography>
                        <IconButton size="small" onClick={() => setSelectedMonthKey(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Imię i Nazwisko</strong></TableCell>
                                    <TableCell align="right"><strong>Dni</strong></TableCell>
                                    <TableCell align="right"><strong>Godziny</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedMonth.employees.map((emp, i) => (
                                    <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>{emp.name} {emp.surname}</TableCell>
                                        <TableCell align="right">{emp.days}</TableCell>
                                        <TableCell align="right">{emp.hours.toFixed(1)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell><strong>Łącznie</strong></TableCell>
                                    <TableCell align="right"><strong>{selectedMonth.totalDays}</strong></TableCell>
                                    <TableCell align="right"><strong>{selectedMonth.totalHours}</strong></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}

export default AttendanceSummaryReport;
